/* eslint-disable camelcase */
import { createAction } from '@reduxjs/toolkit';
import { AppDispatch, AppState } from 'app/store';
import {
  API_ENDPOINTS,
  PANGO_REDEEM_AUTHORIZATION_TOKEN,
  STORAGE_KEYS,
} from 'utils/constants';
import { setInStorage, logErrors } from 'utils';
import { LooseObjectType } from 'utils/globalTypes';
import { InitialValues } from 'pages/signin';
import { deleteAPI, postAPI } from 'services/api/apiAsync';
import formatEntitlements, {
  formatUserFeatures,
  getAgentIdFromAccessToken,
  getXsrfTokenFromJwt,
  getSessionIdFromAccessToken,
} from 'services/session/utils';
import { doLockVault } from 'services/vault/vaultAsync';
import {
  selectBrandCode,
  selectBrandVaultProject,
} from 'services/brand/brandSelectors';
import { setBrand } from 'services/brand/brandSlice';
import type {
  SessionSliceType,
  TourAttributeUpdate,
  TourUserAttributes,
} from './types';
import {
  setSessionSlice,
  refreshTokenUpdate,
  setTourAttributes,
} from './sessionSlice';
import { selectRefreshToken, selectToken } from './sessionSelectors';

const signOutRequest = createAction('SIGNOUT_REQUEST');

export function storeUserSessionDetails(response: LooseObjectType) {
  return async (dispatch: AppDispatch) => {
    try {
      const { token, accessToken, refreshToken, profile } = response;
      const { missingPii } = profile;
      /* Captured from the sign-in `profile`, which reports the state BEFORE the
         backend stamps this login — so `lastVisitedAt` is null on a genuine
         first visit. This is the dashboard tour's first-visit signal; see the
         field docs on `SessionSliceType`. Read defensively because the
         enrollment / reset-password callers may not carry these fields. */
      const lastVisitedAt = profile?.lastVisitedAt ?? null;
      /* Durable tour flags. Confirmed top-level on the wire (not under
         `profile`, unlike `lastVisitedAt`), and sent as a bare `{}` until the
         user has skipped or opted out at least once.
         The defaults therefore matter: an absent or empty object must read as
         "never skipped, never opted out" so the tour still shows, rather than
         yielding `undefined` and suppressing it. The SSO / masquerade path
         forwards whatever `api/client/sessions/redeem` returned, so it lands
         here on the same terms as password sign-in. */
      const userAttributes: TourUserAttributes = {
        skipTourCount: 0,
        dontShowTour: false,
        ...(response.userAttributes ?? {}),
      };
      const xsrfToken = getXsrfTokenFromJwt(token);
      const entitlements = formatEntitlements(
        accessToken,
      ) as SessionSliceType['entitlements'];
      const features = formatUserFeatures(entitlements);
      const agentId = getAgentIdFromAccessToken(accessToken);
      const sessionId = getSessionIdFromAccessToken(accessToken);

      // update session and entitlements
      dispatch(
        setSessionSlice({
          token: xsrfToken,
          userLoggedIn: true,
          agentId,
          entitlements,
          accessToken,
          refreshToken,
          features,
          missingPii,
          lastVisitedAt,
          userAttributes,
          sessionId,
        }),
      );
      return undefined;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/**
 * Persists ONE of the tour's flags.
 *
 * Deliberately partial: the caller sends only the attribute the user actually
 * changed — `{ skipTourCount }` for Skip, `{ dontShowTour: true }` for
 * "Don't show again" and Finish. Sending both would let a Skip overwrite
 * `dontShowTour` with whatever value this client happened to be holding, which
 * could be stale if it changed elsewhere.
 *
 *
 * `POST api/client/users/attribute` answers **204 No Content**, so
 * `makeAPIRequest` resolves to a falsy `response.data`. The value is therefore
 * ignored entirely — never test it for truthiness, or a successful save reads as
 * a failure.
 *
 * The store is updated only after the request resolves (CLAUDE.md rule 6), so a
 * failed save leaves the count as the server still has it. Rejection is
 * re-thrown for the caller to surface: the tour needs to know, because it shows
 * a banner and skips its exit reload on failure.
 */
export function saveTourAttributes(update: TourAttributeUpdate) {
  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(
        postAPI({ url: API_ENDPOINTS.userAttribute, data: update }),
      ).unwrap();
      /* The reducer merges, so the untouched flag keeps its current value. */
      dispatch(setTourAttributes(update));
      return undefined;
    } catch (error) {
      logErrors(error);
      return Promise.reject(error);
    }
  };
}

// user signin
export function createUserSession(values: InitialValues) {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.userSession,
        data: {
          username: values.username,
          password: values.password,
        },
      };
      const response = await dispatch(postAPI(config)).unwrap();

      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

// clear slice data on sign out
export function clearStore() {
  return async (dispatch: AppDispatch, getState: AppState) => {
    const { brand } = getState();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { companyDetails, ...brandDetails } = brand;
    dispatch(signOutRequest()); // To reset the all the slice state to initial values
    dispatch(setBrand(brandDetails)); // To set the brand details
  };
}

// user sign out
export function clearSession() {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const store = getState();
      const refreshToken = selectRefreshToken(store);
      const token = selectToken(store);
      const config = {
        url: API_ENDPOINTS.userSession,
        headers: {
          'X-XSRF-TOKEN': token,
          REFRESH_TOKEN: refreshToken,
        },
      };
      try {
        dispatch(doLockVault());
      } catch (error) {
        logErrors(error);
      }
      try {
        await dispatch(deleteAPI(config)).unwrap();
        dispatch(clearStore());
      } catch (error) {
        dispatch(clearStore());
      }
      return null;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/**
 * Makes POST request to /client/sessions/refresh to refresh the access token
 * Dispatches action REFRESH_TOKEN on success
 */
export function refreshApToken() {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const store = getState();
      const refreshToken = selectRefreshToken(store);
      const token = selectToken(store);
      const config = {
        url: API_ENDPOINTS.refreshToken,
        data: {
          refreshToken,
        },
        headers: {
          'X-XSRF-TOKEN': token,
        },
      };
      const response = await dispatch(postAPI(config)).unwrap();
      dispatch(refreshTokenUpdate(response));

      return response;
    } catch (error) {
      if (error === 401) {
        setInStorage(STORAGE_KEYS.sessionExpired, true);
        dispatch(clearStore());
      }
      return Promise.reject(error);
    }
  };
}

export function verifySignInTokenAndGetSessionTokens(token: string) {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const store = getState();
      const brandCode = selectBrandCode(store);
      const brandVaultProject = selectBrandVaultProject(store);
      const config = {
        url: API_ENDPOINTS.redeemSession,
        method: 'post',
        data: {
          signinToken: token,
        },
        headers: {
          'Content-Type': 'application/json',
          'X-Idg-Brand': brandCode,
          'x-project': brandVaultProject,
          Authorization: PANGO_REDEEM_AUTHORIZATION_TOKEN,
        },
      };

      const response = await dispatch(postAPI(config)).unwrap();
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/**
 * Exchanges the `signin_token` in the URL for a real session. Both the partner
 * SSO hand-off and the CSR masquerade link land here from
 * `/sign-in?signin_token=`.
 *
 * `api/client/sessions/redeem` now answers with the SAME envelope as
 * `POST api/client/sessions` — `token`, `accessToken`, `refreshToken`,
 * `profile` and the durable `userAttributes`. The response is therefore
 * forwarded whole to `storeUserSessionDetails` instead of being rebuilt field
 * by field.
 *
 * The hand-built object this replaces dropped `profile.lastVisitedAt` and
 * `userAttributes`, so every SSO sign-in reached the session slice as
 * `{ skipTourCount: 0, dontShowTour: false }`. That made the dashboard tour's
 * server-side suppression inert for SSO users: `saveTourAttributes` wrote Skip
 * and "Don't show again" to `api/client/users/attribute` correctly, but nothing
 * ever read them back, so the tour returned on the next sign-in however many
 * times it had been dismissed — and always with the first-visit ("built") copy,
 * since `lastVisitedAt` was missing too. Password sign-in was never affected:
 * `pages/signin` passes its response straight through.
 *
 * Defaults remain only where the response may legitimately say nothing:
 * `profile.missingPii`, which the caller destructures, and `token`, whose
 * fallback is the redeem token from the URL. `userAttributes` needs none —
 * `storeUserSessionDetails` already defaults it.
 */
export function masqueradeUserSession(token: string) {
  return async (dispatch: AppDispatch): Promise<LooseObjectType> => {
    try {
      const response = await dispatch(
        verifySignInTokenAndGetSessionTokens(token),
      );

      return {
        ...response,
        token: response.token ?? token,
        /* snake_case fallbacks — the shape the old `auth/session/redeem`
           returned, kept so an environment still pointing at it signs in
           rather than storing `undefined` tokens. */
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
        profile: {
          missingPii: [],
          ...(response.profile ?? {}),
        },
      };
    } catch (error) {
      return Promise.reject(error);
    }
  };
}

/* Function to fetch ChatAgent scoped JWT token */
export function fetchThinSessionDetails() {
  return async (dispatch: AppDispatch) => {
    try {
      const config = {
        url: API_ENDPOINTS.thinSession,
      };

      const response = await dispatch(postAPI(config)).unwrap();
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
}
